import type { CompletionContext, CompletionResult, Completion } from '@codemirror/autocomplete';
import { StateField, type EditorState } from '@codemirror/state';
import { showTooltip, type EditorView, type Tooltip } from '@codemirror/view';
import { symbolsOf, setSymbolsEffect } from './symbolState';
import { findCallContext, type SymbolDef, type SymbolTable } from './symbols';

// --- Completion Data ---

interface ParamInfo {
  name: string;
  info?: string;
}

interface ScadEntry {
  label: string;
  type: string; // 'function' | 'constant' | 'variable' | 'keyword'
  info: string;
  detail?: string; // signature
  params?: ParamInfo[];
}

const SCAD_ENTRIES: ScadEntry[] = [
  // ── 3D Primitives ──
  {
    label: 'cube',
    type: 'function',
    info: 'Rectangular box. Default origin at corner.',
    detail: 'cube(size, center, anchor, fillet, chamfer, edges, $fn)',
    params: [
      { name: 'size', info: 'Number or [x,y,z]' },
      { name: 'center', info: 'Center at origin' },
      { name: 'anchor', info: 'Anchor point at origin' },
      { name: 'fillet', info: 'Round edges (negative = concave)' },
      { name: 'chamfer', info: '45° chamfer edges' },
      { name: 'edges', info: 'Anchor or array of anchors to select edges' },
      { name: '$fn', info: 'Resolution for fillets' },
    ],
  },
  {
    label: 'sphere',
    type: 'function',
    info: 'Sphere centered at origin.',
    detail: 'sphere(r, d, anchor, $fn)',
    params: [
      { name: 'r', info: 'Radius' },
      { name: 'd', info: 'Diameter' },
      { name: 'anchor', info: 'Anchor point at origin' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'cylinder',
    type: 'function',
    info: 'Cylinder or cone along Z axis.',
    detail: 'cylinder(h, r, d, r1, r2, d1, d2, center, anchor, fillet, chamfer, $fn)',
    params: [
      { name: 'h', info: 'Height' },
      { name: 'r', info: 'Radius' },
      { name: 'd', info: 'Diameter' },
      { name: 'r1', info: 'Bottom radius (conical)' },
      { name: 'r2', info: 'Top radius (conical)' },
      { name: 'd1', info: 'Bottom diameter' },
      { name: 'd2', info: 'Top diameter' },
      { name: 'center', info: 'Center on Z' },
      { name: 'anchor', info: 'Anchor point at origin' },
      { name: 'fillet', info: 'Fillet both edges' },
      { name: 'fillet_top', info: 'Fillet top edge' },
      { name: 'fillet_bot', info: 'Fillet bottom edge' },
      { name: 'chamfer', info: 'Chamfer both edges' },
      { name: 'chamfer_top', info: 'Chamfer top edge' },
      { name: 'chamfer_bot', info: 'Chamfer bottom edge' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'polyhedron',
    type: 'function',
    info: 'Arbitrary 3D solid from points and faces.',
    detail: 'polyhedron(points, faces)',
    params: [
      { name: 'points', info: 'Array of [x,y,z] vertices' },
      { name: 'faces', info: 'Array of face vertex index arrays' },
    ],
  },
  {
    label: 'chamfered_cylinder',
    type: 'function',
    info: 'Cylinder with chamfered edges.',
    detail: 'chamfered_cylinder(r, h, chamfer, chamfer_top, chamfer_bot, center, $fn)',
    params: [
      { name: 'r', info: 'Radius' },
      { name: 'd', info: 'Diameter' },
      { name: 'r1', info: 'Bottom radius (conical)' },
      { name: 'r2', info: 'Top radius (conical)' },
      { name: 'h', info: 'Height' },
      { name: 'chamfer', info: 'Chamfer both edges' },
      { name: 'chamfer_top', info: 'Top chamfer' },
      { name: 'chamfer_bot', info: 'Bottom chamfer' },
      { name: 'center', info: 'Center on Z' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'rounded_cube',
    type: 'function',
    info: 'Cube with rounded edges (hull of 8 spheres).',
    detail: 'rounded_cube(size, r, center, $fn)',
    params: [
      { name: 'size', info: 'Number or [x,y,z]' },
      { name: 'r', info: 'Corner radius' },
      { name: 'center', info: 'Center at origin' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'torus',
    type: 'function',
    info: 'Torus (donut shape).',
    detail: 'torus(r1, r2, angle, $fn)',
    params: [
      { name: 'r1', info: 'Major radius' },
      { name: 'r2', info: 'Minor radius' },
      { name: 'angle', info: 'Sweep angle (default 360)' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'pipe',
    type: 'function',
    info: 'Hollow cylinder with wall thickness.',
    detail: 'pipe(h, r, t, r_inner, center, fillet, chamfer, $fn)',
    params: [
      { name: 'h', info: 'Height' },
      { name: 'r', info: 'Outer radius' },
      { name: 't', info: 'Wall thickness' },
      { name: 'r_inner', info: 'Inner radius (alternative to t)' },
      { name: 'center', info: 'Center on Z' },
      { name: 'fillet', info: 'Fillet all corners' },
      { name: 'fillet_top', info: 'Fillet top' },
      { name: 'fillet_bot', info: 'Fillet bottom' },
      { name: 'chamfer', info: 'Chamfer all corners' },
      { name: 'chamfer_top', info: 'Chamfer top' },
      { name: 'chamfer_bot', info: 'Chamfer bottom' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'ellipsoid',
    type: 'function',
    info: 'Ellipsoid with per-axis radii.',
    detail: 'ellipsoid(r, rx, ry, rz, $fn)',
    params: [
      { name: 'r', info: 'Radius (scalar or [rx,ry,rz])' },
      { name: 'rx', info: 'X-axis radius override' },
      { name: 'ry', info: 'Y-axis radius override' },
      { name: 'rz', info: 'Z-axis radius override' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'wedge',
    type: 'function',
    info: 'Triangular wedge in XZ plane extruded along Y.',
    detail: 'wedge(size, center)',
    params: [
      { name: 'size', info: 'Scalar or [w, d, h]' },
      { name: 'center', info: 'Center at origin' },
    ],
  },
  {
    label: 'capsule',
    type: 'function',
    info: 'Pill shape (cylinder with spherical caps).',
    detail: 'capsule(h, r, center, $fn)',
    params: [
      { name: 'h', info: 'Total height including caps' },
      { name: 'r', info: 'Radius' },
      { name: 'center', info: 'Center on Z' },
      { name: '$fn', info: 'Resolution' },
    ],
  },

  // ── Structural Profiles ──
  {
    label: 'l_profile',
    type: 'function',
    info: 'L-shaped structural profile.',
    detail: 'l_profile(w, h, t, l, fillet, chamfer, fillet_outside, chamfer_outside, center, $fn)',
    params: [
      { name: 'w', info: 'Width' },
      { name: 'h', info: 'Height' },
      { name: 't', info: 'Wall thickness' },
      { name: 'l', info: 'Extrusion length' },
      { name: 'fillet', info: 'Inner corner fillet' },
      { name: 'chamfer', info: 'Inner corner chamfer' },
      { name: 'fillet_outside', info: 'Outer corner fillet' },
      { name: 'chamfer_outside', info: 'Outer corner chamfer' },
      { name: 'center', info: 'Center on all axes' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'u_profile',
    type: 'function',
    info: 'U-shaped (channel) structural profile.',
    detail: 'u_profile(w, h, t, l, fillet, chamfer, fillet_outside, chamfer_outside, center, $fn)',
    params: [
      { name: 'w', info: 'Width' },
      { name: 'h', info: 'Height' },
      { name: 't', info: 'Wall thickness' },
      { name: 'l', info: 'Extrusion length' },
      { name: 'fillet', info: 'Inner corner fillet' },
      { name: 'chamfer', info: 'Inner corner chamfer' },
      { name: 'fillet_outside', info: 'Outer corner fillet' },
      { name: 'chamfer_outside', info: 'Outer corner chamfer' },
      { name: 'center', info: 'Center on all axes' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'o_profile',
    type: 'function',
    info: 'Rectangular tube (box section) profile.',
    detail: 'o_profile(w, h, t, l, fillet, chamfer, fillet_outside, chamfer_outside, center, $fn)',
    params: [
      { name: 'w', info: 'Width' },
      { name: 'h', info: 'Height' },
      { name: 't', info: 'Wall thickness' },
      { name: 'l', info: 'Extrusion length' },
      { name: 'fillet', info: 'Inner corner fillet' },
      { name: 'chamfer', info: 'Inner corner chamfer' },
      { name: 'fillet_outside', info: 'Outer corner fillet' },
      { name: 'chamfer_outside', info: 'Outer corner chamfer' },
      { name: 'center', info: 'Center on all axes' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 't_profile',
    type: 'function',
    info: 'T-shaped structural profile.',
    detail: 't_profile(w, h, t, tw, l, fillet, chamfer, center, $fn)',
    params: [
      { name: 'w', info: 'Flange width' },
      { name: 'h', info: 'Total height' },
      { name: 't', info: 'Flange thickness' },
      { name: 'tw', info: 'Web thickness (defaults to t)' },
      { name: 'l', info: 'Extrusion length' },
      { name: 'fillet', info: 'Inner corner fillet' },
      { name: 'chamfer', info: 'Inner corner chamfer' },
      { name: 'center', info: 'Center on all axes' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'i_profile',
    type: 'function',
    info: 'I-beam structural profile.',
    detail: 'i_profile(w, h, t, tw, l, fillet, chamfer, center, $fn)',
    params: [
      { name: 'w', info: 'Flange width' },
      { name: 'h', info: 'Total height' },
      { name: 't', info: 'Flange thickness' },
      { name: 'tw', info: 'Web thickness (defaults to t)' },
      { name: 'l', info: 'Extrusion length' },
      { name: 'fillet', info: 'Inner corner fillet' },
      { name: 'chamfer', info: 'Inner corner chamfer' },
      { name: 'center', info: 'Center on all axes' },
      { name: '$fn', info: 'Resolution' },
    ],
  },

  // ── Gears ──
  {
    label: 'spur_gear',
    type: 'function',
    info: 'Involute spur gear.',
    detail: 'spur_gear(teeth, module, pressure_angle, thickness, center, $fn)',
    params: [
      { name: 'teeth', info: 'Number of teeth' },
      { name: 'module', info: 'Metric module (tooth size)' },
      { name: 'pressure_angle', info: 'Pressure angle (default 20°)' },
      { name: 'thickness', info: 'Gear thickness' },
      { name: 'center', info: 'Center on Z' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'helical_gear',
    type: 'function',
    info: 'Helical gear with angled teeth.',
    detail: 'helical_gear(teeth, module, pressure_angle, helix_angle, thickness, center, $fn)',
    params: [
      { name: 'teeth', info: 'Number of teeth' },
      { name: 'module', info: 'Metric module' },
      { name: 'pressure_angle', info: 'Pressure angle (default 20°)' },
      { name: 'helix_angle', info: 'Helix angle in degrees' },
      { name: 'thickness', info: 'Gear thickness' },
      { name: 'center', info: 'Center on Z' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'herringbone_gear',
    type: 'function',
    info: 'Herringbone (double-helical) gear.',
    detail: 'herringbone_gear(teeth, module, pressure_angle, helix_angle, thickness, center, $fn)',
    params: [
      { name: 'teeth', info: 'Number of teeth' },
      { name: 'module', info: 'Metric module' },
      { name: 'pressure_angle', info: 'Pressure angle (default 20°)' },
      { name: 'helix_angle', info: 'Helix angle in degrees' },
      { name: 'thickness', info: 'Gear thickness' },
      { name: 'center', info: 'Center on Z' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'bevel_gear',
    type: 'function',
    info: 'Bevel gear for angled power transmission.',
    detail: 'bevel_gear(teeth, module, pressure_angle, cone_angle, thickness, center, $fn)',
    params: [
      { name: 'teeth', info: 'Number of teeth' },
      { name: 'module', info: 'Metric module' },
      { name: 'pressure_angle', info: 'Pressure angle (default 20°)' },
      { name: 'cone_angle', info: 'Cone half-angle in degrees' },
      { name: 'thickness', info: 'Gear thickness' },
      { name: 'center', info: 'Center on Z' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'rack',
    type: 'function',
    info: 'Linear gear rack.',
    detail: 'rack(teeth, module, pressure_angle, thickness, height, center, $fn)',
    params: [
      { name: 'teeth', info: 'Number of teeth' },
      { name: 'module', info: 'Metric module' },
      { name: 'pressure_angle', info: 'Pressure angle (default 20°)' },
      { name: 'thickness', info: 'Rack thickness (depth)' },
      { name: 'height', info: 'Tooth height' },
      { name: 'center', info: 'Center at origin' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'worm',
    type: 'function',
    info: 'Worm gear (helical screw).',
    detail: 'worm(module, length, d, starts, pressure_angle, center, $fn)',
    params: [
      { name: 'module', info: 'Metric module' },
      { name: 'length', info: 'Worm length' },
      { name: 'd', info: 'Worm diameter' },
      { name: 'starts', info: 'Number of starts' },
      { name: 'pressure_angle', info: 'Pressure angle (default 20°)' },
      { name: 'center', info: 'Center on Z' },
      { name: '$fn', info: 'Resolution' },
    ],
  },

  // ── Mechanical Components ──
  {
    label: 'thread',
    type: 'function',
    info: 'Helical screw thread.',
    detail: 'thread(d, pitch, length, starts, profile_angle, center, $fn)',
    params: [
      { name: 'd', info: 'Diameter' },
      { name: 'pitch', info: 'Thread pitch' },
      { name: 'length', info: 'Thread length' },
      { name: 'starts', info: 'Number of starts (default 1)' },
      { name: 'profile_angle', info: 'Thread angle (default 60°)' },
      { name: 'center', info: 'Center on Z' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'spring',
    type: 'function',
    info: 'Helical coil spring.',
    detail: 'spring(r, r_wire, pitch, turns, center, $fn)',
    params: [
      { name: 'r', info: 'Coil radius' },
      { name: 'r_wire', info: 'Wire radius' },
      { name: 'pitch', info: 'Distance between coils' },
      { name: 'turns', info: 'Number of turns' },
      { name: 'center', info: 'Center on Z' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'bolt',
    type: 'function',
    info: 'ISO metric hex bolt. Origin at head bottom, shank along +Z.',
    detail: 'bolt(size, length, d, head_h, head_d, pitch, thread_length, chamfer, standard, center, $fn)',
    params: [
      { name: 'size', info: 'Metric string "M1".."M30"' },
      { name: 'length', info: 'Shank length' },
      { name: 'd', info: 'Shaft diameter' },
      { name: 'head_h', info: 'Head height' },
      { name: 'head_d', info: 'Head across-flats' },
      { name: 'pitch', info: 'Thread pitch' },
      { name: 'thread_length', info: 'Threaded portion (0=smooth)' },
      { name: 'chamfer', info: 'Chamfer' },
      { name: 'standard', info: '"din" (default) or "iso"' },
      { name: 'center', info: 'Center on Z' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'nut',
    type: 'function',
    info: 'ISO metric hex nut. Origin at bottom face.',
    detail: 'nut(size, h, d, s, thread, pitch, chamfer, standard, center, $fn)',
    params: [
      { name: 'size', info: 'Metric string "M1".."M30"' },
      { name: 'h', info: 'Height' },
      { name: 'd', info: 'Bore diameter' },
      { name: 's', info: 'Across-flats / wrench size' },
      { name: 'thread', info: 'Boolean: threaded bore' },
      { name: 'pitch', info: 'Thread pitch' },
      { name: 'chamfer', info: 'Chamfer' },
      { name: 'standard', info: '"din" or "iso"' },
      { name: 'center', info: 'Center on Z' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'enclosure',
    type: 'function',
    info: 'Parametric box enclosure with lid and mounting options.',
    detail: 'enclosure(size, wall, r, lid_h, lip_h, lip_gap, mount, screw_size, part, center, $fn)',
    params: [
      { name: 'size', info: '[width, depth, total_height]' },
      { name: 'wall', info: 'Wall thickness (default 2)' },
      { name: 'r', info: 'Corner radius (default 3)' },
      { name: 'fillet_top', info: 'Lid top edge fillet' },
      { name: 'fillet_bottom', info: 'Body bottom edge fillet' },
      { name: 'chamfer_top', info: 'Lid top edge chamfer' },
      { name: 'chamfer_bottom', info: 'Body bottom edge chamfer' },
      { name: 'lid_h', info: 'Lid thickness (default = wall)' },
      { name: 'lip_h', info: 'Lip height (default 2)' },
      { name: 'lip_gap', info: 'Lip clearance (default 0.2)' },
      { name: 'mount', info: '"none", "screw", or "bolt"' },
      { name: 'screw_size', info: 'Metric string e.g. "M3"' },
      { name: 'screw_d', info: 'Override screw diameter' },
      { name: 'screw_inset', info: 'Corner to screw distance' },
      { name: 'boss_r', info: 'Mount pillar radius' },
      { name: 'hole_depth', info: 'Mount hole depth (0=through)' },
      { name: 'nut_pocket', info: 'Hex nut recess on floor' },
      { name: 'mount_points', info: 'Array of [x,y] for custom pillars' },
      { name: 'part', info: '"assembly", "body", or "lid"' },
      { name: 'center', info: 'Center at origin' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'standoff',
    type: 'function',
    info: 'Mounting pillar with optional hole.',
    detail: 'standoff(h, r, d, hole, hole_d, hole_depth, screw_size, center, anchor, $fn)',
    params: [
      { name: 'h', info: 'Height (default 10)' },
      { name: 'r', info: 'Outer radius (auto from screw_size)' },
      { name: 'd', info: 'Outer diameter' },
      { name: 'hole', info: '"pilot", "through", or "none"' },
      { name: 'hole_d', info: 'Hole diameter' },
      { name: 'hole_depth', info: 'Depth from top (0=full)' },
      { name: 'screw_size', info: 'Metric string e.g. "M3"' },
      { name: 'center', info: 'Center on Z' },
      { name: 'anchor', info: 'Anchor point' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'shell',
    type: 'function',
    info: 'Hollows out child shapes with uniform wall thickness.',
    detail: 'shell(thickness, open) { children }',
    params: [
      { name: 'thickness', info: 'Wall thickness (default 2)' },
      { name: 'open', info: 'Anchor face to remove (e.g. TOP)' },
    ],
  },

  // ── 2D Primitives ──
  {
    label: 'circle',
    type: 'function',
    info: '2D circle. Use inside linear_extrude or rotate_extrude.',
    detail: 'circle(r, d, $fn)',
    params: [
      { name: 'r', info: 'Radius' },
      { name: 'd', info: 'Diameter' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'square',
    type: 'function',
    info: '2D rectangle.',
    detail: 'square(size, center)',
    params: [
      { name: 'size', info: '[width, height]' },
      { name: 'center', info: 'Center at origin' },
    ],
  },
  {
    label: 'polygon',
    type: 'function',
    info: '2D polygon from points.',
    detail: 'polygon(points, paths)',
    params: [
      { name: 'points', info: 'Array of [x,y] vertices' },
      { name: 'paths', info: 'Array of vertex index arrays (for holes)' },
    ],
  },
  {
    label: 'regular_polygon',
    type: 'function',
    info: 'Regular n-sided polygon.',
    detail: 'regular_polygon(r, n, $fn)',
    params: [
      { name: 'r', info: 'Circumradius' },
      { name: 'n', info: 'Number of sides (default 6)' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'hexagon',
    type: 'function',
    info: 'Regular hexagon (alias for regular_polygon with n=6).',
    detail: 'hexagon(r)',
    params: [{ name: 'r', info: 'Circumradius' }],
  },
  {
    label: 'slot',
    type: 'function',
    info: 'Stadium/obround shape (rectangle with semicircle caps).',
    detail: 'slot(w, h, center, $fn)',
    params: [
      { name: 'w', info: 'Width' },
      { name: 'h', info: 'Height' },
      { name: 'center', info: 'Center at origin' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'star',
    type: 'function',
    info: 'Star polygon with alternating radii.',
    detail: 'star(points, r_outer, r_inner)',
    params: [
      { name: 'points', info: 'Number of points (default 5)' },
      { name: 'r_outer', info: 'Outer radius' },
      { name: 'r_inner', info: 'Inner radius (default 40% of outer)' },
    ],
  },

  {
    label: 'text',
    type: 'function',
    info: '2D text geometry from a string. Use with linear_extrude for 3D text.',
    detail: 'text(text, size, halign, valign, spacing, $fn)',
    params: [
      { name: 'text', info: 'The text string to render' },
      { name: 'size', info: 'Font size in model units (default 10)' },
      { name: 'halign', info: '"left", "center", or "right"' },
      { name: 'valign', info: '"baseline", "bottom", "center", or "top"' },
      { name: 'spacing', info: 'Letter spacing multiplier (default 1)' },
      { name: '$fn', info: 'Segments per bezier curve (default 16)' },
    ],
  },

  // ── Boolean Operations ──
  {
    label: 'union',
    type: 'function',
    info: 'Combine children into one shape.',
    detail: 'union() { children }',
  },
  {
    label: 'difference',
    type: 'function',
    info: 'Subtract all children from the first child.',
    detail: 'difference() { base; cutters... }',
  },
  {
    label: 'intersection',
    type: 'function',
    info: 'Keep only the overlap of all children.',
    detail: 'intersection() { children }',
  },
  {
    label: 'group',
    type: 'function',
    info: 'Explicit union. Layout children work on combined bbox.',
    detail: 'group() { children }',
  },

  // ── Transforms ──
  {
    label: 'translate',
    type: 'function',
    info: 'Move children by [x, y, z].',
    detail: 'translate([x, y, z]) children',
    params: [{ name: 'v', info: '[x, y, z] translation vector' }],
  },
  {
    label: 'rotate',
    type: 'function',
    info: 'Rotate children by [x, y, z] degrees.',
    detail: 'rotate([x, y, z]) children',
    params: [{ name: 'a', info: '[x, y, z] rotation in degrees' }],
  },
  {
    label: 'scale',
    type: 'function',
    info: 'Scale children by [sx, sy, sz] or uniform scalar.',
    detail: 'scale([sx, sy, sz]) children',
    params: [{ name: 'v', info: 'Scalar or [sx, sy, sz]' }],
  },

  // ── Extrusions ──
  {
    label: 'linear_extrude',
    type: 'function',
    info: 'Extrude 2D profile along Z axis.',
    detail: 'linear_extrude(height, twist, slices, scale, center) children',
    params: [
      { name: 'height', info: 'Extrusion height' },
      { name: 'twist', info: 'Twist angle in degrees' },
      { name: 'slices', info: 'Number of slices for twist' },
      { name: 'scale', info: 'Top scale factor' },
      { name: 'center', info: 'Center on Z' },
    ],
  },
  {
    label: 'rotate_extrude',
    type: 'function',
    info: 'Revolve 2D profile around Z axis.',
    detail: 'rotate_extrude(angle, $fn) children',
    params: [
      { name: 'angle', info: 'Sweep angle (default 360)' },
      { name: '$fn', info: 'Resolution' },
    ],
  },
  {
    label: 'tapered_extrude',
    type: 'function',
    info: 'Linear extrusion whose height varies linearly along an axis. Equivalent to extruding the profile to max(h_start, h_end) and intersecting with a tilted ceiling. Optional chamfer/fillet args are baked into the flat extrusion before the wedge cut, giving clean horizontal-edge treatment.',
    detail: 'tapered_extrude(h_start, h_end, length, axis="x", chamfer/fillet[_top/_bot], $fn) children',
    params: [
      { name: 'h_start', info: 'Height at the lower end of the chosen axis (mm)' },
      { name: 'h_end', info: 'Height at the upper end of the chosen axis (mm)' },
      { name: 'length', info: 'Taper length in mm; default = profile bbox extent along axis' },
      { name: 'axis', info: '"x" (default) or "y" — direction the height tapers along' },
      { name: 'chamfer', info: 'Chamfer size on both top + bottom horizontal edges (mm)' },
      { name: 'chamfer_top', info: 'Chamfer size on top horizontal edge only (mm)' },
      { name: 'chamfer_bot', info: 'Chamfer size on bottom horizontal edge only (mm)' },
      { name: 'fillet', info: 'Fillet radius on both top + bottom horizontal edges (mm). Wins over chamfer.' },
      { name: 'fillet_top', info: 'Fillet radius on top horizontal edge only (mm)' },
      { name: 'fillet_bot', info: 'Fillet radius on bottom horizontal edge only (mm)' },
    ],
  },
  {
    label: 'chamfer',
    type: 'function',
    info: 'Linear taper on the top and/or bottom edges of any 3D child. Works best on prismatic shapes (cube, linear_extrude, primitives).',
    detail: 'chamfer(c, top=true, bottom=true, $fn) { child; }',
    params: [
      { name: 'c', info: 'Chamfer edge size in mm' },
      { name: 'top', info: 'Apply to the top edge (default true)' },
      { name: 'bottom', info: 'Apply to the bottom edge (default true)' },
      { name: '$fn', info: 'Slab divisions (default ~fn/12; higher = smoother)' },
    ],
  },
  {
    label: 'fillet',
    type: 'function',
    info: 'Rounded quarter-arc on the top and/or bottom edges of any 3D child. Works best on prismatic shapes.',
    detail: 'fillet(r, top=true, bottom=true, $fn) { child; }',
    params: [
      { name: 'r', info: 'Fillet radius in mm' },
      { name: 'top', info: 'Apply to the top edge (default true)' },
      { name: 'bottom', info: 'Apply to the bottom edge (default true)' },
      { name: '$fn', info: 'Slab divisions (default ~fn/4; higher = smoother arc)' },
    ],
  },

  // ── Hull & Minkowski ──
  {
    label: 'hull',
    type: 'function',
    info: 'Convex hull of all children.',
    detail: 'hull() { children }',
  },
  {
    label: 'minkowski',
    type: 'function',
    info: 'Minkowski sum of children. Rounds edges when one is a sphere.',
    detail: 'minkowski() { children }',
  },

  // ── Layout Modules ──
  {
    label: 'position',
    type: 'function',
    info: 'Place child center at parent anchor point. No reorientation.',
    detail: 'position(anchor, pad, extend, axis, mode)',
    params: [
      { name: 'anchor', info: 'Point on parent bbox (e.g. TOP)' },
      { name: 'pad', info: 'Expand/shrink parent bbox' },
      { name: 'extend', info: 'Fixed offset after positioning' },
      { name: 'axis', info: 'Which axes to apply' },
      { name: 'mode', info: '"add", "cut", or "intersect"' },
    ],
  },
  {
    label: 'align',
    type: 'function',
    info: 'Standalone: move anchor to origin. In parent: flush child face on parent face.',
    detail: 'align(face, alignment, inset, pad, extend, axis, mode)',
    params: [
      { name: 'face', info: 'Face of parent (e.g. TOP)' },
      { name: 'alignment', info: 'Corner alignment (e.g. LEFT+FRONT)' },
      { name: 'inset', info: 'Shift inward along alignment dirs' },
      { name: 'pad', info: 'Expand/shrink parent bbox' },
      { name: 'extend', info: 'Fixed offset after positioning' },
      { name: 'axis', info: 'Which axes to apply' },
      { name: 'mode', info: '"add", "cut", or "intersect"' },
    ],
  },
  {
    label: 'attach',
    type: 'function',
    info: 'Orient child outward from parent face. BOSL2-style attachment.',
    detail: 'attach(parent_anchor, child_anchor, overlap, spin, align, pad, extend, axis, mode)',
    params: [
      { name: 'parent_anchor', info: 'Point on parent bbox' },
      { name: 'child_anchor', info: 'Attachment point on child bbox' },
      { name: 'overlap', info: 'Shift toward parent along normal' },
      { name: 'spin', info: 'Extra rotation around attachment axis' },
      { name: 'align', info: 'Sub-face positioning' },
      { name: 'pad', info: 'Expand/shrink parent bbox' },
      { name: 'extend', info: 'Fixed offset after positioning' },
      { name: 'axis', info: 'Which axes to apply' },
      { name: 'mode', info: '"add", "cut", or "intersect"' },
    ],
  },
  {
    label: 'frame',
    type: 'function',
    info: 'Invisible phantom bounding box for layout. No rendered geometry.',
    detail: 'frame(size) { children }',
    params: [{ name: 'size', info: 'Number or [x,y,z] (always centered)' }],
  },

  // ── Color ──
  {
    label: 'color',
    type: 'function',
    info: 'Apply color and optional opacity to children.',
    detail: 'color(c, alpha) children',
    params: [
      { name: 'c', info: 'Hex string or CSS color name' },
      { name: 'alpha', info: 'Opacity 0-1 (default 1)' },
    ],
  },

  // ── Import ──
  {
    label: 'import',
    type: 'function',
    info: 'Import external geometry file.',
    detail: 'import(file)',
    params: [{ name: 'file', info: 'File path string' }],
  },

  // ── Modules ──
  {
    label: 'children',
    type: 'function',
    info: 'Reference children passed into a module.',
    detail: 'children()',
  },

  // ── Built-in Math Functions ──
  { label: 'sin', type: 'function', info: 'Sine (degrees).', detail: 'sin(deg)' },
  { label: 'cos', type: 'function', info: 'Cosine (degrees).', detail: 'cos(deg)' },
  { label: 'tan', type: 'function', info: 'Tangent (degrees).', detail: 'tan(deg)' },
  { label: 'abs', type: 'function', info: 'Absolute value.', detail: 'abs(x)' },
  { label: 'ceil', type: 'function', info: 'Round up to integer.', detail: 'ceil(x)' },
  { label: 'floor', type: 'function', info: 'Round down to integer.', detail: 'floor(x)' },
  { label: 'round', type: 'function', info: 'Round to nearest integer.', detail: 'round(x)' },
  { label: 'sqrt', type: 'function', info: 'Square root.', detail: 'sqrt(x)' },
  { label: 'pow', type: 'function', info: 'Power (x^y).', detail: 'pow(x, y)' },
  { label: 'min', type: 'function', info: 'Minimum of two values.', detail: 'min(a, b)' },
  { label: 'max', type: 'function', info: 'Maximum of two values.', detail: 'max(a, b)' },
  { label: 'ln', type: 'function', info: 'Natural logarithm.', detail: 'ln(x)' },
  { label: 'log', type: 'function', info: 'Base-10 logarithm.', detail: 'log(x)' },
  { label: 'len', type: 'function', info: 'Length of array or string.', detail: 'len(v)' },
  { label: 'concat', type: 'function', info: 'Concatenate arrays.', detail: 'concat(a, b)' },

  // ── Anchor Constants ──
  { label: 'CENTER', type: 'constant', info: 'Bounding box center [0, 0, 0].', detail: '[0, 0, 0]' },
  { label: 'TOP', type: 'constant', info: 'Top face (Z+).', detail: '[0, 0, 1]' },
  { label: 'BOTTOM', type: 'constant', info: 'Bottom face (Z-).', detail: '[0, 0, -1]' },
  { label: 'LEFT', type: 'constant', info: 'Left face (X-).', detail: '[-1, 0, 0]' },
  { label: 'RIGHT', type: 'constant', info: 'Right face (X+).', detail: '[1, 0, 0]' },
  { label: 'FRONT', type: 'constant', info: 'Front face (Y-).', detail: '[0, -1, 0]' },
  { label: 'BACK', type: 'constant', info: 'Back face (Y+).', detail: '[0, 1, 0]' },

  // ── Axis Constants ──
  { label: 'X', type: 'constant', info: 'X axis.', detail: '[1, 0, 0]' },
  { label: 'Y', type: 'constant', info: 'Y axis.', detail: '[0, 1, 0]' },
  { label: 'Z', type: 'constant', info: 'Z axis.', detail: '[0, 0, 1]' },
  { label: 'XY', type: 'constant', info: 'X and Y axes.', detail: '[1, 1, 0]' },
  { label: 'XZ', type: 'constant', info: 'X and Z axes.', detail: '[1, 0, 1]' },
  { label: 'YZ', type: 'constant', info: 'Y and Z axes.', detail: '[0, 1, 1]' },

  // ── Special Variables ──
  { label: '$fn', type: 'variable', info: 'Fragment number — controls curved shape resolution.', detail: 'number' },
  { label: 'PI', type: 'constant', info: 'Pi (3.14159...).', detail: '3.14159265...' },

  // ── Keywords ──
  { label: 'for', type: 'keyword', info: 'Loop over range or array.', detail: 'for (i = [start:end]) { ... }' },
  { label: 'if', type: 'keyword', info: 'Conditional statement.', detail: 'if (cond) { ... } else { ... }' },
  { label: 'else', type: 'keyword', info: 'Else branch of conditional.' },
  { label: 'module', type: 'keyword', info: 'Define a reusable module.', detail: 'module name(params) { ... }' },
  { label: 'true', type: 'keyword', info: 'Boolean true.' },
  { label: 'false', type: 'keyword', info: 'Boolean false.' },
];

// Build lookup map for hover tooltips
const entryByLabel = new Map<string, ScadEntry>();
for (const e of SCAD_ENTRIES) {
  entryByLabel.set(e.label, e);
}

// Build param lookup: module name → param names (for context-aware completion)
const paramsByModule = new Map<string, ParamInfo[]>();
for (const e of SCAD_ENTRIES) {
  if (e.params && e.params.length > 0) {
    paramsByModule.set(e.label, e.params);
  }
}

// Pre-build CM Completion objects
const completionItems: Completion[] = SCAD_ENTRIES.map((e) => ({
  label: e.label,
  type: e.type,
  detail: e.detail,
  info: e.info,
}));

// --- Completion Source ---

/**
 * Find the enclosing module call at a given position.
 * Returns the module name if the cursor is inside `moduleName(...)`, otherwise null.
 */
function findEnclosingModule(doc: string, pos: number): string | null {
  // Walk backward from pos to find an unmatched '('
  let depth = 0;
  for (let i = pos - 1; i >= 0; i--) {
    const ch = doc[i];
    if (ch === ')') {
      depth++;
    } else if (ch === '(') {
      if (depth === 0) {
        // Found the unmatched '(' — extract word before it
        let end = i;
        // Skip whitespace before '('
        let j = i - 1;
        while (j >= 0 && (doc[j] === ' ' || doc[j] === '\t')) j--;
        if (j < 0) return null;
        end = j + 1;
        // Walk back through identifier chars
        while (j >= 0 && /[a-zA-Z0-9_$]/.test(doc[j])) j--;
        const name = doc.slice(j + 1, end);
        return name || null;
      }
      depth--;
    }
    // Stop at semicolons or braces (we've left the call context)
    if (ch === ';' || ch === '{' || ch === '}') return null;
  }
  return null;
}

/** Completion entries for the user's own modules, functions and variables. */
function userCompletions(t: SymbolTable): Completion[] {
  const out: Completion[] = [];
  const add = (d: SymbolDef, type: string) => out.push({
    label: d.name, type, detail: d.signature, info: d.doc, boost: 1,
  });
  for (const d of t.modules.values()) add(d, 'class');
  for (const d of t.functions.values()) add(d, 'function');
  for (const d of t.variables.values()) add(d, 'variable');
  return out;
}

/** Parameters of a callee: the user's definition wins over the built-in catalog. */
function paramsFor(name: string, t: SymbolTable): ParamInfo[] | null {
  const user = t.modules.get(name) ?? t.functions.get(name);
  if (user) return user.params.map(p => ({ name: p.name, info: p.defaultText !== undefined ? `default ${p.defaultText}` : undefined }));
  return paramsByModule.get(name) ?? null;
}

export function scadCompletionSource(context: CompletionContext): CompletionResult | null {
  const word = context.matchBefore(/[a-zA-Z_$][a-zA-Z0-9_$]*/);
  if (!word && !context.explicit) return null;

  const from = word ? word.from : context.pos;
  const prefix = word ? word.text : '';
  const symbols = symbolsOf(context.state);
  const user = userCompletions(symbols);
  const userLabels = new Set(user.map(u => u.label));
  // User definitions shadow catalog entries of the same name
  const all = [...user, ...completionItems.filter(c => !userLabels.has(c.label))];

  // Check if we're inside a module call for param completion
  const doc = context.state.doc.toString();
  const enclosingModule = findEnclosingModule(doc, from);

  if (enclosingModule) {
    const params = paramsFor(enclosingModule, symbols);
    if (params && params.length > 0) {
      // Offer param names + regular completions
      const paramCompletions: Completion[] = params
        .filter((p) => p.name.toLowerCase().startsWith(prefix.toLowerCase()))
        .map((p) => ({
          label: p.name,
          type: 'property',
          detail: p.info,
          apply: p.name + ' = ',
        }));

      const regular = all.filter((c) =>
        c.label.toLowerCase().startsWith(prefix.toLowerCase()),
      );

      return {
        from,
        options: [...paramCompletions, ...regular],
        validFor: /^[a-zA-Z_$][a-zA-Z0-9_$]*$/,
      };
    }
  }

  // Regular completions
  if (!prefix && !context.explicit) return null;

  return {
    from,
    options: all,
    validFor: /^[a-zA-Z_$][a-zA-Z0-9_$]*$/,
  };
}

// --- Hover Tooltip ---

function getWordAt(doc: string, pos: number): { word: string; from: number; to: number } | null {
  if (pos < 0 || pos >= doc.length) return null;
  // Check if pos is on a word character
  if (!/[a-zA-Z_$0-9]/.test(doc[pos])) return null;

  let from = pos;
  let to = pos;
  while (from > 0 && /[a-zA-Z_$0-9]/.test(doc[from - 1])) from--;
  while (to < doc.length - 1 && /[a-zA-Z_$0-9]/.test(doc[to + 1])) to++;
  to++; // exclusive end

  const word = doc.slice(from, to);
  // Must start with letter, _, or $
  if (!/^[a-zA-Z_$]/.test(word)) return null;
  return { word, from, to };
}

export function scadHoverTooltip(view: EditorView, pos: number): Tooltip | null {
  const doc = view.state.doc.toString();
  const hit = getWordAt(doc, pos);
  if (!hit) return null;

  const t = symbolsOf(view.state);
  const userDef = t.modules.get(hit.word) ?? t.functions.get(hit.word) ?? t.variables.get(hit.word);
  if (userDef) return userHoverTooltip(userDef, hit.from, hit.to);

  const entry = entryByLabel.get(hit.word);
  if (!entry) return null;

  return {
    pos: hit.from,
    end: hit.to,
    above: true,
    create() {
      const dom = document.createElement('div');
      dom.className = 'scad-hover-tooltip';

      // Name
      const nameEl = document.createElement('div');
      nameEl.className = 'scad-hover-name';
      nameEl.textContent = entry.label;
      dom.appendChild(nameEl);

      // Signature
      if (entry.detail) {
        const sigEl = document.createElement('div');
        sigEl.className = 'scad-hover-sig';
        sigEl.textContent = entry.detail;
        dom.appendChild(sigEl);
      }

      // Description
      const descEl = document.createElement('div');
      descEl.className = 'scad-hover-desc';
      descEl.textContent = entry.info;
      dom.appendChild(descEl);

      // Params
      if (entry.params && entry.params.length > 0) {
        const paramsEl = document.createElement('div');
        paramsEl.className = 'scad-hover-params';
        const title = document.createElement('div');
        title.className = 'scad-hover-params-title';
        title.textContent = 'Parameters:';
        paramsEl.appendChild(title);

        for (const p of entry.params) {
          const row = document.createElement('div');
          row.className = 'scad-hover-param';
          const name = document.createElement('span');
          name.className = 'scad-hover-param-name';
          name.textContent = p.name;
          row.appendChild(name);
          if (p.info) {
            const info = document.createElement('span');
            info.className = 'scad-hover-param-info';
            info.textContent = ' \u2014 ' + p.info;
            row.appendChild(info);
          }
          paramsEl.appendChild(row);
        }
        dom.appendChild(paramsEl);
      }

      return { dom };
    },
  };
}

function userHoverTooltip(def: SymbolDef, from: number, to: number): Tooltip {
  return {
    pos: from,
    end: to,
    above: true,
    create() {
      const dom = document.createElement('div');
      dom.className = 'scad-hover-tooltip';
      const sigEl = document.createElement('div');
      sigEl.className = 'scad-hover-sig';
      sigEl.textContent = def.signature;
      dom.appendChild(sigEl);
      if (def.doc) {
        const descEl = document.createElement('div');
        descEl.className = 'scad-hover-desc';
        descEl.textContent = def.doc;
        dom.appendChild(descEl);
      }
      if (def.kind !== 'variable') {
        const hint = document.createElement('div');
        hint.className = 'scad-hover-desc scad-hover-hint';
        hint.textContent = 'F12 / Ctrl-click: go to definition';
        dom.appendChild(hint);
      }
      return { dom };
    },
  };
}

// --- Signature help -------------------------------------------------------

/** Text of the argument the cursor is in (from its start to `pos`). */
function currentArgText(doc: string, openParen: number, pos: number): string {
  let depth = 0;
  let start = openParen + 1;
  for (let i = openParen + 1; i < pos; i++) {
    const ch = doc[i];
    if (ch === '"') { i++; while (i < pos && !(doc[i] === '"' && doc[i - 1] !== '\\')) i++; continue; }
    if (ch === '(' || ch === '[') depth++;
    else if (ch === ')' || ch === ']') depth--;
    else if (ch === ',' && depth === 0) start = i + 1;
  }
  return doc.slice(start, pos);
}

const WINDOW = 4000;

function computeSignatureTooltip(state: EditorState): Tooltip | null {
  const sel = state.selection.main;
  if (!sel.empty) return null;
  const pos = sel.head;
  const base = Math.max(0, pos - WINDOW);
  const doc = state.doc.sliceString(base, pos);
  const call = findCallContext(doc, doc.length);
  if (!call) return null;
  const params = paramsFor(call.name, symbolsOf(state));
  if (!params || params.length === 0) return null;

  const named = currentArgText(doc, call.openParen, doc.length).match(/^\s*([A-Za-z_$][\w$]*)\s*=/);
  let active = named ? params.findIndex(p => p.name === named[1]) : call.argIndex;
  if (active >= params.length) active = -1;

  return {
    pos: base + call.openParen,
    above: true,
    strictSide: true,
    arrow: false,
    create() {
      const dom = document.createElement('div');
      dom.className = 'scad-signature-help';
      dom.appendChild(document.createTextNode(`${call.name}(`));
      params.forEach((p, i) => {
        if (i > 0) dom.appendChild(document.createTextNode(', '));
        const span = document.createElement('span');
        span.textContent = p.name;
        if (i === active) span.className = 'scad-sig-active';
        dom.appendChild(span);
      });
      dom.appendChild(document.createTextNode(')'));
      const info = active >= 0 ? params[active].info : undefined;
      if (info) {
        const d = document.createElement('div');
        d.className = 'scad-sig-info';
        d.textContent = info;
        dom.appendChild(d);
      }
      return { dom };
    },
  };
}

/** Shows the callee's parameter list (current one highlighted) while typing inside `name(…)`. */
export const signatureHelp = StateField.define<Tooltip | null>({
  create: computeSignatureTooltip,
  update(value, tr) {
    if (!tr.docChanged && !tr.selection && !tr.effects.some(e => e.is(setSymbolsEffect))) return value;
    return computeSignatureTooltip(tr.state);
  },
  provide: f => showTooltip.from(f),
});
